/**
 * The Ynex building blocks this app is made of.
 *
 * Every page is assembled from these, and each one is the template's own markup with the
 * template's own class names, taken from its component pages: box from Cards, the stat
 * card from the CRM dashboard, alerts from Alerts, the modal from Modals, badges from
 * Badges, the table from Tables, the offcanvas from the CRM contacts page, and the row
 * actions and pagination from the product list. The dropdown and date picker are in
 * fields.tsx. Nothing here is a design of its own. Keeping them in one
 * file means a page cannot drift from the template by writing its own version of a card.
 */

import { type ReactNode, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Box, the template's card
// ---------------------------------------------------------------------------

export function Box({
  title,
  count,
  actions,
  children,
  footer,
  className = "",
  bodyClassName = "",
}: {
  readonly title?: ReactNode;
  /** A number shown in the template's round badge right after the title. */
  readonly count?: number;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly className?: string;
  readonly bodyClassName?: string;
}): React.ReactElement {
  return (
    <div className={`box ${className}`}>
      {title || actions ? (
        <div className="box-header justify-between flex-wrap gap-2">
          {title ? (
            <div className="box-title">
              {title}
              {count === undefined ? null : (
                <span className="badge bg-light text-default rounded-full ms-1 text-[0.75rem] align-middle">{count}</span>
              )}
            </div>
          ) : (
            <div />
          )}
          {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={`box-body ${bodyClassName}`}>{children}</div>
      {footer ? <div className="box-footer border-t-0">{footer}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat card, from the CRM dashboard
// ---------------------------------------------------------------------------

export type Tone = "primary" | "secondary" | "success" | "warning" | "danger" | "info";

/*
 * Whole class names, written out. Tailwind only generates a class it can find spelled out
 * in the source, so a name assembled at run time, like "bg-" + tone, would silently get no
 * CSS at all. Every tone the template uses is listed here in full instead.
 */
const SOLID: Record<Tone, string> = {
  primary: "bg-primary",
  secondary: "bg-secondary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
};

const SOFT: Record<Tone, string> = {
  primary: "bg-primary/10 text-primary",
  secondary: "bg-secondary/10 text-secondary",
  success: "bg-success/10 text-success",
  warning: "bg-warning/10 text-warning",
  danger: "bg-danger/10 text-danger",
  info: "bg-info/10 text-info",
};

const TEXT: Record<Tone, string> = {
  primary: "text-primary",
  secondary: "text-secondary",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
};

export function StatCard({
  label,
  value,
  icon,
  tone = "primary",
  note,
  noteTone,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly icon: string;
  readonly tone?: Tone;
  readonly note?: ReactNode;
  readonly noteTone?: Tone;
}): React.ReactElement {
  // Cards sit side by side, so they are all as tall as the tallest: one with a note under
  // its figure must not leave the one beside it looking cut short. The gap between rows is
  // the template's own 1.5rem, moved from the card's margin into padding around it, so the
  // card can fill the height it is given without spilling into the row below.
  return (
    <div className="h-full pb-6">
      <div className="box overflow-hidden h-full !mb-0">
        <div className="box-body h-full">
          <div className="flex items-top justify-between h-full">
            <div>
              <span
                className={`!text-[0.8rem] !w-[2.5rem] !h-[2.5rem] !leading-[2.5rem] !rounded-full inline-flex items-center justify-center ${SOLID[tone]}`}
              >
                <i className={`${icon} text-[1rem] text-white`} />
              </span>
            </div>
            <div className="flex-grow ms-4 flex flex-col">
              <p className="text-[#8c9097] dark:text-white/50 text-[0.813rem] mb-0">{label}</p>
              <h4 className="font-semibold text-[1.5rem] !mb-2 ">{value}</h4>
              {/* Always a line, even with nothing to say, so the figures across a row sit at
                  the same height whether their card has a note or not. */}
              <p
                className={`mt-auto mb-0 text-[0.75rem] ${noteTone ? `${TEXT[noteTone]} font-semibold` : "text-[#8c9097] dark:text-white/50"}`}
              >
                {note ?? " "}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Alerts, the template's "customized alerts with icons"
// ---------------------------------------------------------------------------

const ALERT_ICONS: Record<Tone, string> = {
  primary: "ri-information-line",
  secondary: "ri-information-line",
  info: "ri-information-line",
  success: "ri-checkbox-circle-line",
  warning: "ri-error-warning-line",
  danger: "ri-close-circle-line",
};

export function Alert({
  tone,
  children,
  onClose,
}: {
  readonly tone: Tone;
  readonly children: ReactNode;
  readonly onClose?: (() => void) | undefined;
}): React.ReactElement {
  return (
    <div className={`alert alert-${tone} fade show custom-alert-icon shadow-sm flex items-center`} role="alert">
      <i className={`${ALERT_ICONS[tone]} text-[1.25rem] me-2 shrink-0`} />
      <div className="grow">{children}</div>
      {onClose ? (
        <button type="button" className="ms-2 inline-flex rounded-sm" aria-label="Dismiss" onClick={onClose}>
          <i className="ri-close-line text-[1rem]" />
        </button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

/** The soft badge the template uses in its tables: bg-tone/10 with the tone as text. */
export function Badge({ tone, children }: { readonly tone: Tone | "light"; readonly children: ReactNode }): React.ReactElement {
  if (tone === "light") {
    return <span className="badge bg-light text-default">{children}</span>;
  }
  return <span className={`badge ${SOFT[tone]}`}>{children}</span>;
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

function isTopDialog(element: HTMLElement | null): boolean {
  const own = element?.closest('[role="dialog"]');
  if (!own) return false;
  const all = Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]'));
  const highest = all.sort((left, right) => Number(getComputedStyle(left).zIndex || 0) - Number(getComputedStyle(right).zIndex || 0)).at(-1);
  return highest === own;
}

/**
 * The template's modal, opened and closed by React rather than by Preline, so that a
 * confirm button can wait for a reason to be typed. The markup and classes are the
 * template's; "open" is the class Preline itself puts on the overlay to show it.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  size = "md",
  overPanel = false,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly size?: "md" | "lg" | "xl";
  /** Opened from inside a side panel, whose z-index (150) is above a modal's own (60). */
  readonly overPanel?: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}): React.ReactElement | null {
  const box = useRef<HTMLDivElement>(null);
  // Callers pass a new function on every render. Reading it through a ref keeps the effect
  // below to one run per opening, so typing never moves the focus back to the first field.
  const close = useRef(onClose);
  close.current = onClose;
  const shown = useShown(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      // An open dropdown or date picker inside takes the first Escape to close itself.
      if (event.key === "Escape" && !event.defaultPrevented && isTopDialog(box.current)) close.current();
    };
    document.addEventListener("keydown", onKey);
    // Focus the first field of the body, so the reason can be typed straight away.
    const body = box.current?.querySelector(".ti-modal-body");
    body?.querySelector<HTMLElement>("input:not([disabled]), textarea, select")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* Preline's own backdrop classes. Preline puts it one below the modal, whose z-index is 60. */}
      <div className={`fixed inset-0 ${overPanel ? "z-[159]" : "z-[59]"} bg-gray-900/50 dark:bg-neutral-900/80`} onClick={onClose} />
      <div
        className={`hs-overlay ti-modal ${overPanel ? "!z-[160]" : ""} ${shown ? "open" : ""} size-full fixed top-0 start-0 overflow-x-hidden overflow-y-auto`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // The overlay covers the whole screen, so a click beside the box lands here rather
        // than on the backdrop underneath. Close on that, but not on clicks inside the box.
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        {/* The template's default width, or the large modal from its mail app. */}
        <div
          className={`ti-modal-box mt-7 ease-out ${size === "lg" ? "lg:!max-w-4xl lg:w-full m-3 lg:!mx-auto" : size === "xl" ? "lg:!max-w-6xl lg:w-full m-3 lg:!mx-auto" : ""}`}
          ref={box}
        >
          <div className="ti-modal-content max-h-[calc(100dvh-3.5rem)]">
            <div className="ti-modal-header">
              <h6 className="modal-title text-[1rem] font-semibold">{title}</h6>
              <button type="button" className="!text-[1rem] !font-semibold !text-defaulttextcolor" onClick={onClose}>
                <span className="sr-only">Close</span>
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="ti-modal-body px-4 overflow-y-auto">{children}</div>
            {footer ? <div className="ti-modal-footer">{footer}</div> : null}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * False on the first frame and true from the next, so an overlay mounted already open still
 * gets the template's own transition from its closed state to "open".
 */
function useShown(open: boolean): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!open) {
      setShown(false);
      return;
    }
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);
  return shown;
}

// ---------------------------------------------------------------------------
// Offcanvas, the template's right-hand panel
// ---------------------------------------------------------------------------

/**
 * The template's right offcanvas, for looking into one row without leaving the list, as
 * its CRM contacts page does. Its body has no side padding of its own; the sections inside
 * bring theirs, the same as in the template.
 */
export function Offcanvas({
  title,
  onClose,
  children,
  wide = false,
}: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly wide?: boolean;
}): React.ReactElement {
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const shown = useShown(true);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !event.defaultPrevented && isTopDialog(panel.current)) close.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      {/* Preline's backdrop, one below the offcanvas, whose z-index is 150. */}
      <div className="fixed inset-0 z-[149] bg-gray-900/50 dark:bg-neutral-900/80" onClick={onClose} />
      <div
        className={`hs-overlay ti-offcanvas ti-offcanvas-right ${wide ? "!max-w-[40rem]" : ""} ${shown ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panel}
      >
        <div className="ti-offcanvas-header">
          <h5 className="ti-offcanvas-title">{title}</h5>
          <button
            type="button"
            className="ti-btn flex-shrink-0 p-0 !mb-0 transition-none text-gray-500 hover:text-gray-700 dark:text-white/50 dark:hover:text-white/80"
            onClick={onClose}
          >
            <span className="sr-only">Close</span>
            <i className="ri-close-line text-[1.125rem]" />
          </button>
        </div>
        <div className="ti-offcanvas-body !h-[calc(100%-3.5rem)] !pb-0">{children}</div>
      </div>
    </>
  );
}

/** One section of an offcanvas, divided the way the template's contact panel is. */
export function PanelSection({
  title,
  children,
  last = false,
}: {
  readonly title?: string;
  readonly children: ReactNode;
  readonly last?: boolean;
}): React.ReactElement {
  return (
    <div className={`p-6 ${last ? "" : "border-b border-dashed border-defaultborder dark:border-defaultborder/10"}`}>
      {title ? <p className="text-[.875rem] mb-3 font-semibold">{title}</p> : null}
      {children}
    </div>
  );
}

/** A button's label while it works: the template's loading spinner, then the word. */
export function Working({ label }: { readonly label: string }): React.ReactElement {
  return (
    <>
      <i className="ri-loader-2-fill text-[1rem] animate-spin" aria-hidden="true" />
      {label}
    </>
  );
}

/** The footer of a form in a modal: cancel on the left of the one thing it saves. */
export function ModalActions({
  busy,
  disabled = false,
  label,
  onCancel,
  onSave,
}: {
  readonly busy: boolean;
  readonly disabled?: boolean;
  readonly label: string;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}): React.ReactElement {
  return (
    <>
      <button type="button" className="ti-btn ti-btn-light" onClick={onCancel}>
        Cancel
      </button>
      <button type="button" className="ti-btn ti-btn-primary-full" disabled={busy || disabled} onClick={onSave}>
        {busy ? <Working label="Saving" /> : label}
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

/** The template's hoverable table. */
export function Table({
  head,
  children,
  compact = false,
}: {
  readonly head: ReactNode;
  readonly children: ReactNode;
  /**
   * Narrow columns, and words that wrap onto a second line, so a wide list fits without
   * scrolling sideways (D, 15 October 2026: "just squeeze the UI, and if too long, can use
   * 2 lines").
   */
  readonly compact?: boolean;
}): React.ReactElement {
  return (
    <div className="table-responsive">
      <table
        className={`table table-hover min-w-full ti-custom-table-hover ${
          compact ? "[&_td]:!px-2 [&_th]:!px-2 [&_td]:!whitespace-normal [&_th]:whitespace-nowrap" : "whitespace-nowrap"
        }`}
      >
        <thead>
          <tr className="border-b border-defaultborder">{head}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Th({ children, end = false }: { readonly children?: ReactNode; readonly end?: boolean }): React.ReactElement {
  return (
    <th scope="col" className={end ? "!text-end" : "text-start"}>
      {children}
    </th>
  );
}

/*
 * The square action buttons from the template's product list: a soft tone that fills in
 * on hover. Written out in full for Tailwind, as above.
 */
const ACTION: Record<Tone, string> = {
  primary: "bg-primary/10 text-primary hover:bg-primary hover:text-white hover:border-primary",
  secondary: "bg-secondary/10 text-secondary hover:bg-secondary hover:text-white hover:border-secondary",
  success: "bg-success/10 text-success hover:bg-success hover:text-white hover:border-success",
  warning: "bg-warning/10 text-warning hover:bg-warning hover:text-white hover:border-warning",
  danger: "bg-danger/10 text-danger hover:bg-danger hover:text-white hover:border-danger",
  info: "bg-info/10 text-info hover:bg-info hover:text-white hover:border-info",
};

/**
 * A row action: the template's square icon button, named by its tooltip. The tooltip says
 * `tip` when there is one (D's wording, 4 October 2026); `label` stays its accessible name.
 */
export function IconAction({
  icon,
  label,
  tip,
  tone,
  onClick,
  disabled = false,
}: {
  readonly icon: string;
  readonly label: string;
  /** What the tooltip shows, when it should say more than the label. */
  readonly tip?: string | undefined;
  readonly tone: Tone;
  readonly onClick: () => void;
  readonly disabled?: boolean;
}): React.ReactElement {
  // Rows arrive after the page has loaded, so their tooltips are wired up as they appear.
  useEffect(() => {
    window.HSStaticMethods?.autoInit(["tooltip"]);
  }, []);

  return (
    <div className="hs-tooltip ti-main-tooltip [--placement:top]">
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        className={`hs-tooltip-toggle ti-btn ti-btn-wave !gap-0 !m-0 !h-[1.75rem] !w-[1.75rem] text-[0.8rem] ${ACTION[tone]}`}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        <i className={icon} />
        <span
          className="hs-tooltip-content ti-main-tooltip-content py-1 px-2 !bg-black !text-xs !font-medium !text-white shadow-sm"
          role="tooltip"
        >
          {tip ?? label}
        </span>
      </button>
    </div>
  );
}

/**
 * The same button, for something the server hands over as a file.
 *
 * A PDF cannot come back through the JSON client, so the browser is sent straight at the
 * address and the session cookie rides along. It has to be a real link for that.
 */
export function IconLink({
  icon,
  label,
  tip,
  tone,
  href,
}: {
  readonly icon: string;
  readonly label: string;
  /** What the tooltip shows, when it should say more than the label. */
  readonly tip?: string | undefined;
  readonly tone: Tone;
  readonly href: string;
}): React.ReactElement {
  useEffect(() => {
    window.HSStaticMethods?.autoInit(["tooltip"]);
  }, []);

  return (
    <div className="hs-tooltip ti-main-tooltip [--placement:top]">
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={label}
        className={`hs-tooltip-toggle ti-btn ti-btn-wave !gap-0 !m-0 !h-[1.75rem] !w-[1.75rem] text-[0.8rem] ${ACTION[tone]}`}
        onClick={(event) => event.stopPropagation()}
      >
        <i className={icon} />
        <span
          className="hs-tooltip-content ti-main-tooltip-content py-1 px-2 !bg-black !text-xs !font-medium !text-white shadow-sm"
          role="tooltip"
        >
          {tip ?? label}
        </span>
      </a>
    </div>
  );
}

/** The row of actions at the end of a table row. */
export function Actions({ children }: { readonly children: ReactNode }): React.ReactElement {
  return <div className="flex flex-row items-center justify-end !gap-2 text-[0.9375rem]">{children}</div>;
}

export const PAGE_SIZE = 10;

/** One page of a list, and the page it is on, back to the first when the list shrinks. */
export function usePaged<T>(items: readonly T[]): {
  readonly rows: readonly T[];
  readonly page: number;
  readonly pages: number;
  readonly setPage: (page: number) => void;
} {
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  return { rows: items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE), page: current, pages, setPage };
}

/**
 * The page numbers to show: the first, the last, and the two either side of the current
 * page, with a gap (null) where numbers are left out. Fifty pages stay one row wide.
 */
export function pageNumbers(page: number, pages: number): (number | null)[] {
  const shown = [...new Set([1, pages, page - 1, page, page + 1])]
    .filter((number) => number >= 1 && number <= pages)
    .sort((a, b) => a - b);
  const out: (number | null)[] = [];
  for (const number of shown) {
    const previous = out[out.length - 1];
    if (typeof previous === "number" && number - previous > 1) out.push(number - previous === 2 ? previous + 1 : null);
    out.push(number);
  }
  return out;
}

/** The template's table footer: how many are shown, and its pagination. */
export function Pager({
  total,
  page,
  pages,
  onPage,
  pageSize = PAGE_SIZE,
}: {
  readonly total: number;
  readonly page: number;
  readonly pages: number;
  readonly onPage: (page: number) => void;
  /** Rows per page, when the pages come from the server rather than from usePaged. */
  readonly pageSize?: number;
}): React.ReactElement | null {
  if (total === 0) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return (
    <div className="sm:flex items-center justify-between flex-wrap gap-2 mt-4">
      <div className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-2 sm:mb-0">
        Showing {first} to {last} of {total}
      </div>
      {pages > 1 ? (
        <nav aria-label="Pages">
          <ul className="ti-pagination mb-0 flex flex-row rounded-sm text-[1rem] !ps-0">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link !py-[0.375rem] !px-[0.75rem]"
                disabled={page === 1}
                onClick={() => onPage(page - 1)}
              >
                Previous
              </button>
            </li>
            {pageNumbers(page, pages).map((number, index) =>
              number === null ? (
                <li key={`gap-${index}`} className="page-item disabled">
                  <span className="page-link !py-[0.375rem] !px-[0.75rem]">...</span>
                </li>
              ) : (
              <li key={number} className="page-item">
                <button
                  type="button"
                  className={`page-link !py-[0.375rem] !px-[0.75rem] ${number === page ? "active" : ""}`}
                  aria-current={number === page ? "page" : undefined}
                  onClick={() => onPage(number)}
                >
                  {number}
                </button>
              </li>
              ),
            )}
            <li className={`page-item ${page === pages ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link !text-primary !py-[0.375rem] !px-[0.75rem]"
                disabled={page === pages}
                onClick={() => onPage(page + 1)}
              >
                Next
              </button>
            </li>
          </ul>
          {/* A page by its number, for a long list (D, 18 October 2026: "I have to click many times to go to page 31"). */}
          {pages > 7 ? (
            <form
              className="flex items-center gap-1 mt-2 justify-end"
              onSubmit={(event) => {
                event.preventDefault();
                const wanted = Number(new FormData(event.currentTarget).get("page"));
                if (Number.isInteger(wanted) && wanted >= 1 && wanted <= pages) onPage(wanted);
              }}
            >
              <label className="text-[0.75rem] text-[#8c9097]" htmlFor={`go-page-${pages}`}>Go to page</label>
              <input id={`go-page-${pages}`} name="page" type="number" min={1} max={pages} className="form-control !w-[4.5rem] !py-1" placeholder={String(page)} />
              <button type="submit" className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0">Go</button>
            </form>
          ) : null}
        </nav>
      ) : null}
    </div>
  );
}

/**
 * The template's light tabs: a row of buttons, the chosen one on a pale primary ground.
 * Held by React, since what is under each tab is a form with its own state.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  readonly tabs: readonly { readonly value: T; readonly label: string }[];
  readonly value: T;
  readonly onChange: (value: T) => void;
}): React.ReactElement {
  return (
    <nav className="flex flex-wrap gap-2" aria-label="Tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={tab.value === value}
          className={`py-[0.4rem] px-2 text-sm font-medium text-center rounded-sm hover:text-primary ${
            tab.value === value ? "bg-primary/10 text-primary" : "text-defaulttextcolor dark:text-white/70"
          }`}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  );
}

/** The two-line cell the template uses for a name with a detail under it. */
export function TwoLine({ main, sub }: { readonly main: ReactNode; readonly sub?: ReactNode }): React.ReactElement {
  return (
    <div>
      <div className="leading-none font-semibold">{main}</div>
      {sub ? <div className="leading-none mt-1 text-[0.6875rem] text-[#8c9097] dark:text-white/50">{sub}</div> : null}
    </div>
  );
}

/** What a table shows before it has anything in it. */
export function Empty({ icon = "ri-inbox-line", children }: { readonly icon?: string; readonly children: ReactNode }): React.ReactElement {
  return (
    <div className="text-center py-8">
      <span className="avatar avatar-lg avatar-rounded bg-light !text-[#8c9097] dark:!text-white/50 mb-3">
        <i className={`${icon} text-[1.5rem]`} />
      </span>
      <p className="text-[#8c9097] dark:text-white/50 text-[0.813rem] mb-0">{children}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------

export function Field({
  id,
  label,
  hint,
  children,
  className = "",
}: {
  readonly id: string;
  readonly label: string;
  readonly hint?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}): React.ReactElement {
  return (
    <div className={className}>
      <label htmlFor={id} className="form-label">
        {label}
      </label>
      {children}
      {hint ? <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">{hint}</p> : null}
    </div>
  );
}

/** Loading, in the template's spinner. */
export function Loading(): React.ReactElement {
  return (
    <div className="flex items-center justify-center py-10 text-[#8c9097]">
      <div className="ti-spinner !w-[1.5rem] !h-[1.5rem] text-primary me-2" role="status" aria-label="loading" />
      Loading
    </div>
  );
}

/** "1 batch", "2 batches": a count with its noun, for the badges beside box titles. */
export function countOf(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}
